import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { getToolById, getToolWorkspacesByToolId } from "@/db/tools"
import { v4 as uuidv4 } from "uuid"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('realtime.tools', supabase)

type Row = Tables<'tools'>
type WsRow = Tables<'tool_workspaces'>

const appliesToWorkspace = async (workspaceId:string | null, id: string): Promise<boolean> => {
	if (!workspaceId) return false
	const toolWorkspaces = await getToolWorkspacesByToolId(supabase, id)
	return toolWorkspaces.workspaces.some(ws => ws.id === workspaceId)
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 * @param workspaceId
 * @param setTools
 * @returns
 */
export function makeToolChangeHandler(
	setTools: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `tool-changes-${uuid}`,
		channelName: `tool-changes-${uuid}`,
		events: ['UPDATE'],
		schema: 'public',
		table: 'tools',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			if (payload.eventType === 'UPDATE') {
				const tool = payload.new as Row
				//here not checking for workspace since will update only if found and it avoid async db call
				setTools(prev => {
					const exists = prev.some(p => p.id === tool.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling tool update:${printObject(tool)}`)
					return prev.map(p => p.id === tool.id ? tool : p)
				}
				)
			}
		},
		inactivityThreshold: 1_800_000
	}
}

/**
 * This method doesn't handle update since the relationship join table is not designed to mutate in that way
 * @param workspaceId
 * @param setTools
 * @returns
 */
export function makeToolWorkspaceHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setTools: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<WsRow> {
	const uuid = uuidv4()
	return {
		key: `tool-ws-${uuid}`,
		channelName: `tool-ws-${uuid}`,
		events: ['INSERT', 'DELETE'],
		schema: 'public',
		table: 'tool_workspaces',
		handler: async (payload: RealtimePostgresChangesPayload<WsRow>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") {
				const toolWs = payload.new as WsRow
				const id = toolWs.tool_id
				if (await appliesToWorkspace(workspaceId, id)) {
					const tool = await getToolById(supabase, id)
					if (tool) {
						setTools(prev => {
							if (prev.some(p => p.id === tool.id)) return prev
							logger.info(`Handling tool Insert:${printObject(tool)}`)
							return [...prev, tool]
						})
					}
				}
			}
			else if (payload.eventType == "DELETE") {
				const toolWs = payload.old as WsRow
				const id = toolWs.tool_id
				setTools(prev => {
					const exists = prev.some(p => p.id === id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling tool Delete:${id}`)
					return prev.filter(p => p.id !== id)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}